import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings'
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined'
import ContactsOutlined from '@mui/icons-material/ContactsOutlined'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import KeyboardOutlined from '@mui/icons-material/KeyboardOutlined'
import LightModeOutlined from '@mui/icons-material/LightModeOutlined'
import MailOutline from '@mui/icons-material/MailOutlined'
import MenuOpen from '@mui/icons-material/MenuOpen'
import Menu from '@mui/icons-material/Menu'
import SearchIcon from '@mui/icons-material/Search'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import { Avatar, Badge, Box, ButtonBase, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import { useState, type ReactElement, type RefObject } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFolders, useMe, useUpdateSettings } from '../api/hooks'
import { colorFor, initials } from '../lib/color'
import { Wordmark } from '../lib/Wordmark'
import { useUi } from '../store/ui'
import { useTokens } from '../theme'
import { AccountMenu } from './AccountMenu'
import { appOf, type AppId } from './apps'
import { SearchBox } from './SearchBox'
import { Sidebar } from './Sidebar'

export const DOCK_EXPANDED = 264
export const DOCK_COLLAPSED = 76

interface AppItem { id: AppId | 'admin'; label: string; to: string; icon: ReactElement }

/**
 * The left dock: brand, app switcher, search, compose, mailboxes and account in one column on the canvas.
 * There is no top bar on desktop; the content surface floats to the right of the dock.
 */
export function Dock({ collapsed, searchRef }: { collapsed: boolean; searchRef: RefObject<HTMLInputElement | null> }) {
  const t = useTokens()
  const theme = useTheme()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const me = useMe()
  const folders = useFolders()
  const updateSettings = useUpdateSettings()
  const { openCompose, toggleSidebar, setShortcutsHelp } = useUi()
  const [account, setAccount] = useState<HTMLElement | null>(null)
  const email = me.data?.email ?? ''
  const dark = theme.palette.mode === 'dark'
  const unread = folders.data?.find((f) => f.role === 'inbox')?.unseen ?? 0
  const current: AppId | 'admin' = pathname.startsWith('/admin') ? 'admin' : appOf(pathname)
  const items: AppItem[] = [
    { id: 'mail', label: 'Mail', to: '/mail/f/inbox', icon: <MailOutline fontSize="small" /> },
    { id: 'calendar', label: 'Calendar', to: '/calendar', icon: <CalendarMonthOutlined fontSize="small" /> },
    { id: 'contacts', label: 'Contacts', to: '/contacts', icon: <ContactsOutlined fontSize="small" /> },
    ...(me.data?.role === 'ADMIN' ? [{ id: 'admin' as const, label: 'Admin', to: '/admin', icon: <AdminPanelSettings fontSize="small" /> }] : []),
  ]
  const focusSearch = () => { if (collapsed) toggleSidebar(); setTimeout(() => searchRef.current?.focus(), 0) }

  return (
    <Box sx={{ width: collapsed ? DOCK_COLLAPSED : DOCK_EXPANDED, flexShrink: 0, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: t.rail, pt: 1.5, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: collapsed ? 0 : 1.5, justifyContent: collapsed ? 'center' : 'space-between', minHeight: 40 }}>
        {collapsed ? (
          <Tooltip title="Expand sidebar" placement="right"><IconButton aria-label="Main menu" onClick={toggleSidebar}><Menu /></IconButton></Tooltip>
        ) : (
          <>
            <Box component="a" href="/" onClick={(e) => { e.preventDefault(); navigate('/mail/f/inbox') }} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary', textDecoration: 'none', minWidth: 0 }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '10px', bgcolor: '#fff', border: 1, borderColor: 'divider', display: 'grid', placeItems: 'center', flexShrink: 0 }}><Box component="img" src="/logo.png" alt="" sx={{ width: 22, height: 22 }} /></Box>
              <Wordmark size={20} />
            </Box>
            <Tooltip title="Collapse sidebar"><IconButton aria-label="Main menu" onClick={toggleSidebar}><MenuOpen /></IconButton></Tooltip>
          </>
        )}
      </Box>

      <Box component="nav" aria-label="Apps" sx={{ mx: collapsed ? 'auto' : 1.5, mt: 1.5, p: '3px', display: 'flex', flexDirection: collapsed ? 'column' : 'row', gap: '2px', borderRadius: '14px', bgcolor: t.hover }}>
        {items.map((a) => {
          const active = a.id === current
          const wide = active && !collapsed
          return (
            <Tooltip key={a.id} title={a.label} placement={collapsed ? 'right' : 'bottom'}>
              <ButtonBase onClick={() => navigate(a.to)} aria-label={a.label} aria-current={active ? 'page' : undefined}
                sx={{ flex: wide ? '1 1 auto' : '0 0 auto', height: 36, minWidth: 40, px: wide ? 1.25 : 0, gap: 0.75, justifyContent: 'center', borderRadius: '11px', color: active ? t.accent : 'text.secondary', bgcolor: active ? 'background.paper' : 'transparent', boxShadow: active ? 1 : 'none', fontWeight: 700, transition: 'background-color 150ms, color 150ms', '&:hover': { color: t.accent }, '&:focus-visible': { outline: '2px solid', outlineColor: t.accent } }}>
                <Badge color="error" badgeContent={a.id === 'mail' ? unread : 0} max={99} sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16, padding: '0 4px', top: -3, right: -5 } }}>{a.icon}</Badge>
                {wide && <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{a.label}</Typography>}
              </ButtonBase>
            </Tooltip>
          )
        })}
      </Box>

      <Box sx={{ px: collapsed ? 0 : 1.5, mt: 1.5, display: 'flex', justifyContent: 'center', '& > form': { flex: 1 } }}>
        {collapsed ? (
          <Tooltip title="Search (/)" placement="right"><IconButton aria-label="Search mail" onClick={focusSearch} sx={{ width: 40, height: 40, border: 1, borderColor: 'divider', borderRadius: '10px', bgcolor: 'background.paper' }}><SearchIcon fontSize="small" /></IconButton></Tooltip>
        ) : (
          <SearchBox inputRef={searchRef} />
        )}
      </Box>

      <Box sx={{ px: collapsed ? 0 : 1.5, mt: 1.25, display: 'flex', justifyContent: 'center' }}>
        {collapsed ? (
          <Tooltip title="Compose (c)" placement="right"><IconButton aria-label="Compose" onClick={() => openCompose()} sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: t.composeBg, color: t.composeFg, '&:hover': { bgcolor: t.composeBg, filter: 'brightness(0.94)' } }}><EditOutlined fontSize="small" /></IconButton></Tooltip>
        ) : (
          <ButtonBase onClick={() => openCompose()} aria-label="Compose" sx={{ width: '100%', height: 44, px: 1.75, gap: 1.25, justifyContent: 'flex-start', borderRadius: '12px', bgcolor: t.composeBg, color: t.composeFg, fontWeight: 700, fontSize: 14, transition: 'filter 150ms', '&:hover': { filter: 'brightness(0.94)' }, '&:focus-visible': { outline: '2px solid', outlineColor: t.accent, outlineOffset: 2 } }}>
            <EditOutlined fontSize="small" />
            <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Compose</Box>
            <Typography component="kbd" aria-hidden variant="caption" sx={{ fontFamily: 'inherit', fontWeight: 700, opacity: 0.8, border: 1, borderColor: 'currentcolor', borderRadius: '6px', px: 0.75, lineHeight: 1.5 }}>C</Typography>
          </ButtonBase>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, mt: 1.5 }}>{current === 'mail' && <Sidebar collapsed={collapsed} />}</Box>

      <Box sx={{ p: collapsed ? 1.25 : 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: collapsed ? 'center' : 'stretch', borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', flexDirection: collapsed ? 'column' : 'row', gap: 0.25 }}>
          <Tooltip title="Settings" placement={collapsed ? 'right' : 'top'}><IconButton aria-label="Settings" onClick={() => navigate('/settings')} sx={{ color: pathname.startsWith('/settings') ? t.accent : undefined }}><SettingsOutlined fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'} placement={collapsed ? 'right' : 'top'}><IconButton aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'} onClick={() => updateSettings.mutate({ theme: dark ? 'light' : 'dark' })}>{dark ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}</IconButton></Tooltip>
          <Tooltip title="Keyboard shortcuts (?)" placement={collapsed ? 'right' : 'top'}><IconButton aria-label="Keyboard shortcuts" onClick={() => setShortcutsHelp(true)}><KeyboardOutlined fontSize="small" /></IconButton></Tooltip>
        </Box>
        <ButtonBase aria-label="Account menu" onClick={(e) => setAccount(e.currentTarget)} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: collapsed ? 0 : 0.75, borderRadius: '12px', justifyContent: 'flex-start', width: collapsed ? 'auto' : '100%', '&:hover': { bgcolor: t.hover }, '&:focus-visible': { outline: '2px solid', outlineColor: t.accent } }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: colorFor(email), fontSize: 14 }}>{initials(null, email)}</Avatar>
          {!collapsed && <Typography noWrap variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>{email}</Typography>}
        </ButtonBase>
        <AccountMenu anchor={account} onClose={() => setAccount(null)} email={email} placement="above" />
      </Box>
    </Box>
  )
}
